import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Table components (shadcn/ui style)
 *
 * A set of components for building accessible data tables, restyled for the
 * Institutional Console redesign (DESIGN-SPEC.md §4.3/§7): `--surface-raised`
 * header background, 10px uppercase 600-weight `--text-faint` header labels,
 * 28px header row / 33px body rows, `--hairline` dividers, `--surface-hover`
 * row hover, and full `<th scope="col">` + `aria-sort` semantics on
 * `SortableTableHead` for screen readers (§7 a11y checklist). Row actions
 * (see `RowIconAction` in `./button.jsx`) are intentionally NEVER hidden
 * behind hover-only CSS anywhere in this file — §4.3/§7 both require them
 * always visible for accessibility (mouse-hover-only interaction excludes
 * touch/keyboard users).
 *
 * @example
 * <Table>
 *   <TableCaption>A list of your recent invoices.</TableCaption>
 *   <TableHeader>
 *     <TableRow>
 *       <TableHead className="w-[100px]">Invoice</TableHead>
 *       <TableHead>Status</TableHead>
 *       <TableHead className="text-right">Amount</TableHead>
 *     </TableRow>
 *   </TableHeader>
 *   <TableBody>
 *     <TableRow>
 *       <TableCell className="font-medium">INV001</TableCell>
 *       <TableCell>Paid</TableCell>
 *       <TableCell className="text-right">$250.00</TableCell>
 *     </TableRow>
 *   </TableBody>
 *   <TableFooter>
 *     <TableRow>
 *       <TableCell colSpan={2}>Total</TableCell>
 *       <TableCell className="text-right">$2,500.00</TableCell>
 *     </TableRow>
 *   </TableFooter>
 * </Table>
 */

const Table = React.forwardRef(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn("bg-surface-raised [&_tr]:border-b [&_tr]:border-hairline [&_tr]:hover:bg-transparent", className)}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t border-hairline bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

// §4.3: 1px --hairline dividers, --surface-hover on row hover, whole row
// clickable where a detail exists (callers add onClick + cursor-pointer).
// 33px row height (8px vertical padding baked into TableCell below, not here,
// so custom cell padding overrides still compose correctly).
const TableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b border-hairline transition-colors duration-[120ms] ease-out hover:bg-surface-hover data-[state=selected]:bg-muted focus-within:bg-surface-hover",
      className
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

// §4.3/§7: 10px uppercase 600-weight --text-faint labels, 28px header row
// height, real `scope="col"` (required for screen readers — callers should
// NOT override away the scope attribute). `sortDirection`/`ariaSort` let
// plain (non-sortable) TableHead cells opt into aria-sort without pulling in
// the full SortableTableHead click/icon behavor.
const TableHead = React.forwardRef(
  ({ className, ariaSort, scope = "col", ...props }, ref) => (
    <th
      ref={ref}
      scope={scope}
      aria-sort={ariaSort}
      className={cn(
        "h-7 px-3 text-left align-middle text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--text-faint,var(--color-text-disabled))] [&:has([role=checkbox])]:pr-0",
        className
      )}
      {...props}
    />
  )
);
TableHead.displayName = "TableHead";

// §4.3/§2.4: 33px row height via py-2 (16px) + line-height ~ matches spec's
// "8px v-padding" target at the component's base 12.5px row font size.
// Numeric cells: callers pass `className="text-right font-mono tabular-nums"`
// (or use value-display.jsx's CurrencyValue/PercentValue directly) — kept
// generic here per CC3 (don't scatter font-mono, but don't hardcode it into
// every cell either, since text cells like the "Asset"/"Platform" columns
// must stay in the sans family).
const TableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "px-3 py-2 align-middle text-[12.5px] text-foreground [&:has([role=checkbox])]:pr-0",
      className
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

/**
 * SortableTableHead - Table head with sort indicator
 *
 * §4.3: "sortable columns show ↓ on the active sort" — a single down-chevron
 * glyph marks whichever column is currently sorted (asc or desc); this
 * matches the Holdings.dc.html mockup's "Value ↓" header, which does not
 * flip glyph direction between asc/desc. §7: real `aria-sort` state
 * (`ascending`/`descending`/`none`) is set on the underlying `<th>` so
 * screen readers announce sort state; the `<th>` keeps its native
 * `columnheader` role (per the WAI-ARIA "sortable table" pattern, the sort
 * trigger is a `<button>` NESTED inside the `<th>`, not the `<th>` itself
 * turned into a button — overriding a `<th>`'s implicit role would break
 * assistive tech that relies on `columnheader`/`scope="col"` semantics).
 * The inner button gives keyboard users (Tab + Enter/Space) the same
 * activation path as a mouse click, satisfying §7's "reachable without a
 * mouse" requirement without sacrificing table semantics.
 *
 * @param {Object} props
 * @param {'asc'|'desc'|null} props.sortDirection - Current sort direction
 * @param {function} props.onSort - Sort handler
 */
const SortableTableHead = React.forwardRef(
  ({ className, children, sortDirection, onSort, ...props }, ref) => {
    const ariaSort =
      sortDirection === "asc" ? "ascending" : sortDirection === "desc" ? "descending" : "none";

    return (
      <TableHead
        ref={ref}
        ariaSort={ariaSort}
        className={cn("p-0", className)}
        {...props}
      >
        <button
          type="button"
          onClick={onSort}
          className="flex h-7 w-full cursor-pointer select-none items-center gap-1 px-3 text-left transition-colors duration-[120ms] ease-out hover:text-[var(--text-secondary,var(--color-text-secondary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset"
        >
          {children}
          {sortDirection && (
            <span className="ml-1" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </span>
          )}
        </button>
      </TableHead>
    );
  }
);
SortableTableHead.displayName = "SortableTableHead";

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  SortableTableHead,
};
